import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import GUI from 'lil-gui';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import particlesVertexShader from './shaders/particles/vertex.glsl';
import particlesFragmentShader from './shaders/particles/fragment.glsl';
import ScrollSmoother from './lib/ScrollSmoother.js';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { MeshSurfaceSampler } from 'three/addons/math/MeshSurfaceSampler.js';

gsap.registerPlugin(ScrollTrigger, ScrollSmoother);

ScrollSmoother.create({
  smooth: 3,
  effects: true,
  sppeed: 0.4,
  normalizeScroll: true,
});

/**
 * Base
 */
// Debug
const gui = new GUI({ width: 340 });
gui.destroy();

const debugObject = {};
debugObject.clearColor = '#03020d';

let mouse = {
  x: 0,
  y: 0,
};

// Canvas
const canvas = document.querySelector('canvas.webgl');

// Scene
const scene = new THREE.Scene();

// Loaders
const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath('./draco/');
const gltfLoader = new GLTFLoader();
gltfLoader.setDRACOLoader(dracoLoader);

/**
 * Sizes
 */
const sizes = {
  width: window.innerWidth,
  height: window.innerHeight,
  pixelRatio: Math.min(window.devicePixelRatio, 2),
  halfWidth: window.innerWidth / 2,
  halfHeight: window.innerHeight / 2,
};

window.addEventListener('resize', () => {
  // Update sizes
  sizes.width = window.innerWidth;
  sizes.height = window.innerHeight;
  sizes.pixelRatio = Math.min(window.devicePixelRatio, 2);
  sizes.halfWidth = window.innerWidth / 2;
  sizes.halfHeight = window.innerHeight / 2;

  // Materials
  if (particles)
    particles.material.uniforms.uResolution.value.set(
      sizes.width * sizes.pixelRatio,
      sizes.height * sizes.pixelRatio
    );

  // Update camera
  camera.aspect = sizes.width / sizes.height;
  camera.updateProjectionMatrix();

  // Update renderer
  renderer.setSize(sizes.width, sizes.height);
  renderer.setPixelRatio(sizes.pixelRatio);
});

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
  canvas: canvas,
  antialias: true,
});

/**
 * Camera
 */
const camera = new THREE.PerspectiveCamera(35, sizes.width / sizes.height, 0.1, 100);
camera.position.set(0, 0, 12);
scene.add(camera);

renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(sizes.pixelRatio);
renderer.setClearColor(debugObject.clearColor);

/**
 * Spread Random Particle (BG)
 */
const spread = {};
spread.color = '#eaca57';
spread.geometry = new THREE.BufferGeometry();
spread.position = new Float32Array(350 * 3);
spread.rotation = { x: 0, y: 0, z: 0 };

for (let i = 0; i < spread.position.length; i += 3) {
  spread.position[i] = (Math.random() - 0.5) * 50; // x
  spread.position[i + 1] = (Math.random() - 0.5) * 20; // y
  spread.position[i + 2] = (Math.random() - 0.5) * 20; // z
}
spread.geometry.setAttribute(
  'position',
  new THREE.Float32BufferAttribute(spread.position, 3)
);

spread.material = new THREE.ShaderMaterial({
  vertexShader: `
    uniform vec2 uResolution;
    uniform float uSize;
    uniform vec3 uColor;
    
    varying vec3 vColor;

    void main()
    {
      vec4 modelPosition = modelMatrix * vec4(position, 1.0);
      vec4 viewPosition = viewMatrix * modelPosition;
      vec4 projectedPosition = projectionMatrix * viewPosition;
      gl_Position = projectedPosition;

      gl_PointSize = uSize * uResolution.y;
      gl_PointSize *= (1.0 / - viewPosition.z);

      vColor = uColor;
    }
  `,
  fragmentShader: `
    varying vec3 vColor;

    void main()
    {
      vec2 uv = gl_PointCoord;
      float distanceToCenter = length(uv - 0.5);
      float alpha = 0.05 / distanceToCenter - 0.1;

      gl_FragColor = vec4(vColor, alpha);

      #include <tonemapping_fragment>
      #include <colorspace_fragment>
    }
  `,
  uniforms: {
    uSize: new THREE.Uniform(0.2),
    uResolution: new THREE.Uniform(
      new THREE.Vector2(sizes.width * sizes.pixelRatio, sizes.height * sizes.pixelRatio)
    ),
    uColor: new THREE.Uniform(new THREE.Color(spread.color)),
  },
  blending: THREE.AdditiveBlending,
  depthWrite: false,
});

const spreadParticle = new THREE.Points(spread.geometry, spread.material);
scene.add(spreadParticle);

/**
 * Particles
 */
// const models = ['baby_yoda.glb', 'rocket.glb', 'suzanne.glb', 'among_us.glb'];
const models = ['baby_yoda.glb', 'rocket.glb', 'suzanne.glb'];
const particles = {};
particles.index = 0;
particles.limitCount = 6000; // count 최대 허용범위

const positions = [];
let maxCount = 0;

// Set particles position, Get maxCount
for (const model of models) {
  await gltfLoader.loadAsync(`./models/${model}`).then((gltf) => {
    const mesh = new THREE.Mesh(mergeModel(gltf.scene));
    const count = mesh.geometry.attributes.position.count;
    maxCount = Math.max(count, maxCount);

    positions.push(pointification(mesh, count));
  });

  particles.maxCount = maxCount;
}

// Particles Control & Size
particles.positions = [];
particles.sizes = [];
for (const position of positions) {
  const array = position.array;
  const newArray = new Float32Array(particles.maxCount * 3);
  const size = new Float32Array(particles.maxCount);

  const { maxCount, limitCount } = particles;
  const limit = limitCount < maxCount ? limitCount : maxCount;

  // limit 수에 파티클 수 맞춤
  for (let i = 0; i < limit; i++) {
    const i3 = i * 3;

    if (i3 < array.length) {
      newArray[i3] = array[i3];
      newArray[i3 + 1] = array[i3 + 1];
      newArray[i3 + 2] = array[i3 + 2];

      // 파티클 크기 랜덤
      size[i] = Math.random();
    } else {
      // i가 모델링 count 수보다 커지면 이미 존재하는 파티클의 위치중 랜덤으로 할당
      const randomIndex = Math.floor(position.count * Math.random()) * 3;
      newArray[i3] = array[randomIndex];
      newArray[i3 + 1] = array[randomIndex + 1];
      newArray[i3 + 2] = array[randomIndex + 2];

      // i가 모델링 count 수보다 커지면 사이즈 0으로 조정
      size[i] = 0;
    }
  }

  particles.positions.push(new THREE.Float32BufferAttribute(newArray, 3));
  particles.sizes.push(new THREE.BufferAttribute(size, 1));
}

/**
 * Random Particles
 */
const random = {};
random.count = 900;

const rd_position = new Float32Array(particles.maxCount * 3).fill(0);
const rd_size = new Float32Array(particles.maxCount).fill(0);

const { maxCount: max, limitCount: lc } = particles;
const limit = lc < max ? lc : max;

for (let i = 0; i < limit; i++) {
  const i3 = i * 3;
  if (random.count > i3) {
    rd_position[i3] = (Math.random() - 0.5) * 10; // -5 ~ 5 범위
    rd_position[i3 + 1] = (Math.random() - 0.5) * 10; // -5 ~ 5 범위
    rd_position[i3 + 2] = (Math.random() - 0.5) * 10; // -5 ~ 5 범위
    rd_size[i] = Math.random();
  } else {
    rd_position[i3] = rd_position[Math.floor(Math.random() * (i3 - 1))];
    rd_position[i3 + 1] = rd_position[Math.floor(Math.random() * (i3 - 1))]; // -5 ~ 5 범위
    rd_position[i3 + 2] = rd_position[Math.floor(Math.random() * (i3 - 1))]; // -5 ~ 5 범위
    rd_size[i] = 0;
  }
}
random.position = new THREE.Float32BufferAttribute(rd_position, 3);
random.size = new THREE.BufferAttribute(rd_size, 1);
particles.positions.splice(1, 0, random.position);
particles.sizes.splice(1, 0, random.size);

/**
 * Geometry
 */
particles.geometry = new THREE.BufferGeometry();
particles.geometry.setAttribute('position', particles.positions[particles.index]); // position - from
particles.geometry.setAttribute('aPositionTarget', particles.positions[1]); // position - to
particles.geometry.setAttribute('size', particles.sizes[particles.index]); // size - from
particles.geometry.setAttribute('aSize', particles.sizes[1]); // size - to

/**
 * Material
 */
particles.colorA = '#3e49a3';
particles.colorB = '#948038';
particles.material = new THREE.ShaderMaterial({
  vertexShader: particlesVertexShader,
  fragmentShader: particlesFragmentShader,
  uniforms: {
    uSize: new THREE.Uniform(0.6),
    uResolution: new THREE.Uniform(
      new THREE.Vector2(sizes.width * sizes.pixelRatio, sizes.height * sizes.pixelRatio)
    ),
    uProgress: new THREE.Uniform(0),
    uColorA: new THREE.Uniform(new THREE.Color(particles.colorA)),
    uColorB: new THREE.Uniform(new THREE.Color(particles.colorB)),
  },
  blending: THREE.AdditiveBlending,
  depthWrite: false,
});

/**
 * Points
 */
particles.points = new THREE.Points(particles.geometry, particles.material);
particles.points.frustumCulled = false;
scene.add(particles.points);

/**
 * Tweaks
 */
gui.addColor(debugObject, 'clearColor').onChange(() => {
  renderer.setClearColor(debugObject.clearColor);
});
gui.addColor(particles, 'colorA').onChange(() => {
  particles.material.uniforms.uColorA.value.set(particles.colorA);
});
gui.addColor(particles, 'colorB').onChange(() => {
  particles.material.uniforms.uColorB.value.set(particles.colorB);
});
gui
  .add(particles.material.uniforms.uProgress, 'value')
  .min(0)
  .max(1)
  .step(0.001)
  .name('uProgress')
  .listen();
gui
  .addColor(spread, 'color')
  .name('SpreadParticleColor')
  .onChange(() => {
    spread.material.uniforms.uColor.value.set(spread.color);
  });

/**
 * Methods & Event
 */
document.body.addEventListener('pointermove', onPointerMove);
morphing();
scrollListener();

// Merge Model
function mergeModel(model, scale = 1) {
  let gs = [];

  //  traverse: scene의 child 항목들을 반복적으로 검사하는 기능 수행
  model.traverse((child) => {
    // Mesh와 Object3D 차이점?
    // Object3D: 위치, 회전, 크기 등 변환될 수 있으며 주로 컨테이너의 역할로 사용된다. 예를 들어 scene도 Object3D에 포함
    // Mesh: Object3D를 확장하는 동시에 실제 모델의 geometry, matrial 등 형상을 가질 수 있음
    if (child.isMesh) {
      // toNonIndexed: 인덱스가 있는 BufferGeometry의 인덱스가 없는 버전을 리턴
      // let g = child.geometry.clone().toNonIndexed();
      let g = child.geometry.clone();
      for (let a in g.attributes) {
        // position 속성을 제외하고 모두 제거
        if (a != 'position') g.deleteAttribute(a);
      }

      // Matrix: 모든 Object3D의 모든 인스턴스는 matrix를 가지고 있어 위치, 회전, 스케일 등의 정보를 담고 있음
      // applyMatrix4: 매트릭스 변환을 객체에 적용하고 객체의 위치, 회전 및 스케일을 업데이트
      g.applyMatrix4(child.matrixWorld);
      gs.push(g);
    }
  });

  return mergeGeometries(gs).center().scale(scale, scale, scale);
}

// Particle Sampler Point
function pointification(mesh, amount) {
  // MeshSurfaceSampler: mesh 표면에 임의의 랜덤한 점을 샘플링하는 유틸리티 클래스
  let mss = new MeshSurfaceSampler(mesh).build();
  let pointsData = [];
  let v = new THREE.Vector3(); // 파티클 좌표 x, y, z

  // Mesh 표면에서 랜덤하게 샘플링하고 샘플 인스턴스 생성
  for (let i = 0; i < amount; i++) {
    // 임의의 위치에 포지션을 선택
    mss.sample(v);

    // 파티클 좌표를 pointsData 배열에 저장
    // toArray(좌표를 저장할 대상 배열, 시작 인덱스)
    v.toArray(pointsData, i * 3);
  }
  return new THREE.Float32BufferAttribute(pointsData, 3);
}

// Scrolltrigger Morphing
function morphing() {
  const sections = document.querySelectorAll('.section');

  const transform = (index, state) => {
    if (index >= 0) {
      let next = index + 1;

      particles.material.uniforms.uProgress.value = state === 'enter' ? 0 : 1;

      particles.geometry.attributes.position = particles.positions[index];
      particles.geometry.attributes.aPositionTarget = particles.positions[next];
      particles.geometry.attributes.size = particles.sizes[index];
      particles.geometry.attributes.aSize = particles.sizes[next];
      particles.index = index;
    }
  };
  sections.forEach((section, index) => {
    if (index !== sections.length - 1) {
      gsap.to(particles.material.uniforms.uProgress, {
        value: 1,
        ease: 'none',
        scrollTrigger: {
          trigger: section,
          start: 'top top',
          end: 'bottom top',
          scrub: true,
          onEnter: () => transform(index, 'enter'),
          onLeaveBack: () => transform(index - 1, 'enterback'),
        },
      });
    }
  });
}

// Pointer Event
function onPointerMove(event) {
  if (!event.isPrimary) return;

  gsap.to(mouse, {
    x: event.clientX - sizes.halfWidth,
    y: event.clientY - sizes.halfHeight,
    duration: 1,
    ease: 'circ',
  });

  gsap.to('.cursor', {
    x: event.clientX,
    y: event.clientY,
  });
}

function scrollListener() {
  // camera position
  gsap
    .timeline({
      ease: 'circ',
      scrollTrigger: {
        trigger: '#main',
        start: 'top top',
        end: 'bottom 10%',
        scrub: true,
      },
    })
    .to(camera.position, {
      z: 10,
    });
  gsap
    .timeline({
      ease: 'circ',
      scrollTrigger: {
        trigger: '#particle',
        start: 'top top',
        end: 'bottom top',
        scrub: true,
      },
    })
    .to(camera.position, {
      z: 13,
    });

  // spread particles rotation
  gsap
    .timeline({
      ease: 'circ',
      scrollTrigger: {
        trigger: '#main',
        start: 'bottom bottom',
        end: 'bottom top',
        scrub: 5,
      },
    })
    .to(spread.rotation, {
      z: Math.PI / 4,
    });
}

/**
 * Animate
 */
const tick = () => {
  // Spread andom particle update
  const timer = 0.00005 * Date.now();
  for (let i = 0; i < spread.position.length; i += 3) {
    const point = spread.position;
    point[i] = 5 * Math.cos(timer + i);
    point[i + 1] = 5 * Math.sin(timer + i * 1.1);
  }
  spread.geometry.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(spread.position, 3)
  );

  // Mouse effect
  // camera.position.x = (mouse.x - camera.position.x) * 0.001;
  // camera.position.y = (-mouse.y - camera.position.y) * 0.001;
  camera.lookAt(scene.position);

  // 기본 회전 / 포지션 모션
  let baseDegree = Math.PI / 18; // 10도 (Math.PI = 180도)
  let rangeDegree = baseDegree / 2; // 5도
  camera.rotation.z = Math.sin(timer * 7.5) * rangeDegree - baseDegree; // -5 ~ 5도
  camera.position.y = Math.sin(timer * 7.5) * 2;

  // Spread particles rotation
  spread.geometry.rotateZ(spread.rotation.z);

  // Render normal scene
  renderer.render(scene, camera);

  // Call tick again on the next frame
  window.requestAnimationFrame(tick);
};

tick();
